import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@codes/ui-kit/components/ui/alert-dialog';
import { Input } from '@codes/ui-kit/components/ui/input';
import { Label } from '@codes/ui-kit/components/ui/label';
import { AlertTriangle, XCircle } from 'lucide-react';

interface CodesKeyRegenerateDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    keyName: string;
    maskedKey: string;
    isRegenerating: boolean;
    onConfirm: () => void;
}

export const CodesKeyRegenerateDialog = ({
    open,
    onOpenChange,
    keyName,
    maskedKey,
    isRegenerating,
    onConfirm,
}: CodesKeyRegenerateDialogProps) => {
    const { t } = useTranslation();
    const [confirmText, setConfirmText] = useState('');

    const isValid = confirmText === keyName;

    const handleConfirm = () => {
        if (isValid) {
            onConfirm();
        }
    };

    const handleOpenChange = (newOpen: boolean) => {
        if (!isRegenerating) {
            setConfirmText('');
            onOpenChange(newOpen);
        }
    };

    return (
        <AlertDialog open={open} onOpenChange={handleOpenChange}>
            <AlertDialogContent className="max-w-lg">
                <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2.5 text-xl">
                        <AlertTriangle className="h-6 w-6 text-destructive" />
                        {t('codesKeys.regenerateDialog.title')}
                    </AlertDialogTitle>
                    <AlertDialogDescription className="space-y-5 pt-2">
                        <p className="text-base">{t('codesKeys.regenerateDialog.description')}</p>

                        <div className="bg-muted/50 border border-border rounded-lg p-3">
                            <code className="font-mono text-sm font-medium">{maskedKey}</code>
                        </div>

                        <div className="space-y-3 bg-destructive/5 border border-destructive/20 rounded-lg p-4">
                            <p className="font-semibold text-sm flex items-center gap-2">
                                <XCircle className="h-4 w-4 text-destructive" />
                                {t('codesKeys.regenerateDialog.consequences')}:
                            </p>
                            <ul className="space-y-2 text-sm">
                                <li className="flex items-start gap-2">
                                    <span className="text-destructive mt-0.5">•</span>
                                    <span>{t('codesKeys.regenerateDialog.consequence1')}</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-destructive mt-0.5">•</span>
                                    <span>{t('codesKeys.regenerateDialog.consequence2')}</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-destructive mt-0.5">•</span>
                                    <span>{t('codesKeys.regenerateDialog.consequence3')}</span>
                                </li>
                            </ul>
                        </div>

                        <div className="space-y-3 pt-1">
                            <Label htmlFor="confirm-input" className="text-sm font-semibold">
                                {t('codesKeys.regenerateDialog.confirmPrompt')}{' '}
                                <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-semibold">{keyName}</code>
                            </Label>
                            <Input
                                id="confirm-input"
                                value={confirmText}
                                onChange={e => setConfirmText(e.target.value)}
                                placeholder={keyName}
                                disabled={isRegenerating}
                                className="font-mono"
                            />
                        </div>
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel disabled={isRegenerating}>
                        {t('codesKeys.regenerateDialog.cancel')}
                    </AlertDialogCancel>
                    <AlertDialogAction
                        onClick={handleConfirm}
                        disabled={!isValid || isRegenerating}
                        className="bg-destructive hover:bg-destructive/90"
                    >
                        {isRegenerating
                            ? t('codesKeys.regenerateDialog.regenerating')
                            : t('codesKeys.regenerateDialog.regenerate')}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};
